<template>
<div>
  <el-row class="title-bar" type="flex" justify="space-between">
    <div class="vertical-center-box">
      <div class="title-h3 mr-r15">모델링 스케줄 목록</div>
      <el-button size="mini" @click="clickDeleteScheduleButton" :disabled="checkedRows==0">삭제</el-button>
    </div>
    <div>
      <el-button type="primary" size="mini" @click="clickSaveButton" :disabled="!changeScheduleTableDataFlag">저장</el-button>
      <el-button size="mini" @click="clickCancelButton" :disabled="!changeScheduleTableDataFlag">취소</el-button>
      <w-header-search-button size="mini" @click="setSearchToolVisible"></w-header-search-button>
    </div>
  </el-row>
  <el-table-wrapper v-loading="isLoading" :column-default="columnDefault" :data="scheduleTableData" :columns="scheduleTableColumns" ref="scheduleTable" row-class-name="c-dblclick" highlight-current-row :pagination="true" :show-search-tool="showSearchTool"
    :show-custom-header="true" @selection-change="changeCheckedRow" @row-dblclick="dblclickRow" @condition-change="changeTableCondition" remote :total="total">
    <template slot="active-slot" slot-scope="scope">
      <el-switch v-model="scope.row.activeFlag" active-value="Y" inactive-value="N" @change="checkChangeScheduleTableDataFlag"/>
    </template>
    <template slot="execute-slot" slot-scope="scope">
      <i class="fas fa-play-circle text-large-circle text-14 c-pointer" @click="clickExecute(scope.row)"></i>
        <!-- <span style="cursor: pointer" ><i class="far fa-caret-square-right" /></span> -->
    </template>
    <template slot="ipscan-slot" slot-scope="scope">
      <span class="link" @click="openIpScanPopup(scope.row)">{{ scope.row.ipscanCount }}</span>
    </template>
    <template slot="process-rate-slot" slot-scope="scope">
      <el-progress :text-inside="true" :stroke-width="14" :percentage="setProcessRate(scope.row.processRate)" />
    </template>
    <template slot-scope="scope" slot="detail-slot">
      <span><i class="far fa-search-plus c-pointer" @click.stop="onClickDetailButton(scope.row)"></i></span>
    </template>
  </el-table-wrapper>
  <w-ip-scan-popup ref="ipscanPopup" :schedule-data="selectedScheduleData" />
  <w-modeling-result-popup ref="modelingResultPopup"></w-modeling-result-popup>
</div>
</template>

<script>
import moment from 'moment';
import differenceWith from 'lodash/differenceWith';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import concat from 'lodash/concat';
import DeviceRegistrationNmsAPI from '@/api/settings/common/device-registration-nms';
import msgUtil from '@/utils/message';
import { getDateToString } from '@/utils/date';
import { columnDefault, defaultDetailHeaderColumn } from '@/constants/table';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { isEmpty } from '@/utils/basic';
import WIpScanPopup from './popup/IpScanPopup';
import WModelingResultPopup from './popup/ModelingResultPopup';

export default {
  name: 'WSchedule',
  components: {
    WIpScanPopup,
    WModelingResultPopup,
  },
  props: {
    activeTab: String,
  },
  data() {
    return {
      isLoading: false,
      columnDefault,
      type: 'rsctype',
      showSearchTool: false,
      selectedRow: null,
      total: 0,
      scheduleTableData: [],
      scheduleTableOriginData: [],
      changeScheduleTableDataFlag: false,
      checkedRows: [],
      scheduleTask: 'modelingScheduleTask',
      executeScheduleList: [],
      executeScheduleIds: [],
      scheduleTableColumns: [
        {
          align: 'center',
          type: 'selection',
        },
        {
          prop: 'activeFlag',
          label: '활성 여부',
          align: 'center',
          width: 80,
          scopedSlot: 'active-slot',
        },
        {
          prop: 'scheduleName',
          label: '스케줄명',
          width: 250,
          searchable: true,
        },
        {
          prop: 'schedulePeriod',
          label: '수행 주기',
          width: 100,
          searchable: true,
          formatter: this.formatSchedulePeriod,
        },
        {
          prop: 'scheduleContent',
          label: '상세 내용',
          searchable: true,
        },
        {
          prop: 'ipscanCount',
          label: 'IP 대역',
          width: 120,
          scopedSlot: 'ipscan-slot',
        },
        {
          prop: 'lastScheduleDatetime',
          label: '다음 수행 일시',
          width: 150,
          formatter: this.formatNextScheduleDatetime,
        },
        {
          prop: 'managerId',
          label: '등록자',
          sortable: true,
          width: 120,
        },
        {
          prop: 'lastScheduleDatetime',
          label: '최근 수행 일시',
          width: 150,
          formatter: this.getDateFormatter,
        },
        {
          prop: 'processRate',
          label: '진행률',
          scopedSlot: 'process-rate-slot',
          width: 100,
        },
        { ...defaultDetailHeaderColumn, label: '모델링 결과', width: 80, scopedSlot: 'detail-slot' },
        {
          align: 'center',
          label: '즉시수행',
          width: 75,
          scopedSlot: 'execute-slot',
        },
      ],
      selectedScheduleData: {},
    };
  },
  beforeDestroy() {
    this.$taskManager.removeTask(this.scheduleTask);
  },
  methods: {
    loadData() {
      const tableParams = this.$refs.scheduleTable.getParameter();
      this.isLoading = true;
      DeviceRegistrationNmsAPI.getModelingScheduleList(tableParams)
        .then((response) => {
          this.scheduleTableData = cloneDeep(response.data.data);
          this.scheduleTableOriginData = cloneDeep(response.data.data);
          // 수행이 완료되지 않은 모델링의 진행률을 주기적으로 체크한다.
          const uncompletedModelingList = [];
          const uncompletedModelingIds = [];
          forEach(this.scheduleTableData, (item) => {
            if (item.processRate !== 0 && item.processRate < 1 && this.executeScheduleIds.indexOf(item.scheduleId) < 0) {
              uncompletedModelingList.push(item);
              uncompletedModelingIds.push(item.scheduleId);
            }
          });
          if (uncompletedModelingList.length > 0 && uncompletedModelingIds.length > 0) {
            this.executeScheduleList = concat(this.executeScheduleList, uncompletedModelingList);
            this.executeScheduleIds = concat(this.executeScheduleIds, uncompletedModelingIds);
            this.startTaskToCheckModelingProcessRate();
          }

          this.$nextTick(() => {
            this.checkChangeScheduleTableDataFlag();
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    clickSaveButton() {
      const target = '모델링 스케쥴';
      const action = msgUtil.action.SAVE;
      DeviceRegistrationNmsAPI.updateScheduleList(differenceWith(this.scheduleTableData, this.scheduleTableOriginData, isEqual))
        .then((response) => {
          const result = response.data;
          if (result.success === true) {
            this.$message(msgUtil.getResultMsgData(action, target, true));
            this.loadData();
          } else {
            this.$message(msgUtil.getResultMsgData(action, target, false));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        });
    },
    formatterForCheckEmptyValue(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    clickCancelButton() {
      this.loadData();
    },
    setSearchToolVisible() {
      this.showSearchTool = !this.showSearchTool;
    },
    checkChangeScheduleTableDataFlag() {
      this.changeScheduleTableDataFlag = !isEqual(this.scheduleTableData, this.scheduleTableOriginData);
    },
    changeCheckedRow(selection) {
      this.checkedRows = selection;
    },
    clickDeleteScheduleButton() {
      const target = '모델링 스케쥴';
      const action = msgUtil.action.DELETE;

      this.$confirm(...msgUtil.getBasicConfirmData(action))
        .then(() => {
          DeviceRegistrationNmsAPI.deleteScheduleList(this.checkedRows).then((response) => {
            const result = response.data;
            if (result.success === true) {
              this.$message(msgUtil.getResultMsgData(action, target, true));
              this.loadData();
            } else {
              this.$message(msgUtil.getResultMsgData(action, target, false));
            }
          });
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        });
    },
    clickNetinterfaceCount(row) {
      this.$emit('select-netdevice', row);
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    clickAddNetworkButton() {
      this.$refs.addNetworkPopup.$refs.slidingPopup.visible = true;
    },
    clickRegistMagicianButton() {
      this.$emit('click-regist-magician-button');
    },
    dblclickRow(row) {
      DeviceRegistrationNmsAPI.getRegistMagicianInfo({
        scheduleId: row.scheduleId,
      }).then((response) => {
        const registMagicianInfo = response.data.data;
        registMagicianInfo.scheduleData = row;
        registMagicianInfo.scheduleData.checkScheduleFlag = true;

        this.$emit('update-modeling-schedule', registMagicianInfo);
      });
    },
    changeTableCondition() {
      this.loadData();
    },
    clickExecute(row) {
      const scheduleId = row.scheduleId;
      const target = '모델링';
      const action = '수행';
      this.isLoading = true;
      DeviceRegistrationNmsAPI.executeModelingSchedule(scheduleId)
        .then((res) => {
          if (res.data.success) {
            this.$message(msgUtil.getCustomResultMsgData('모델링 스케쥴 수행 중입니다.', true));
            row.lastScheduleDatetime = res.data.data;
            const newScheduleList = [];
            const newScheduleIds = [];

            if (this.executeScheduleIds.indexOf(scheduleId) < 0) {
              newScheduleList.push(row);
              newScheduleIds.push(scheduleId);
            }
            this.executeScheduleList = concat(this.executeScheduleList, newScheduleList);
            this.executeScheduleIds = concat(this.executeScheduleIds, newScheduleIds);

            this.startTaskToCheckModelingProcessRate();
          } else {
            this.$message(msgUtil.getResultMsgData(action, target, false));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    /**
     * 모델링 스케쥴 진행률을 확인하는 Task를 생성하고 수행한다.
     * @author Ju
     * @return {[type]} [description]
     */
    startTaskToCheckModelingProcessRate() {
      this.$taskManager.createTask(
        this.scheduleTask,
        1,
        () => {
          DeviceRegistrationNmsAPI.getModelingScheduleProcessRateList(this.executeScheduleIds).then((response) => {
            if (response.data.success) {
              const result = response.data.data;
              if (result.length < 1) {
                this.$taskManager.removeTask(this.scheduleTask);
                this.executeScheduleIds = [];
                this.executeScheduleList = [];
                this.loadData();
                this.$message(msgUtil.getResultMsgData('완료', '스케쥴 수행', true));
              } else {
                const noCompletedScheduleList = [];
                const noCompletedScheduleIds = [];
                forEach(this.executeScheduleList, (item) => {
                  let completeFlag = true;
                  for (let i = 0; i < result.length; i += 1) {
                    if (item.scheduleId === result[i].scheduleId) {
                      item.processRate = result[i].processRate;
                      noCompletedScheduleList.push(item);
                      noCompletedScheduleIds.push(item.scheduleId);
                      completeFlag = false;
                      break;
                    }
                  }
                  if (completeFlag) {
                    item.processRate = 1;
                  }
                });

                this.executeScheduleIds = noCompletedScheduleIds;
                this.executeScheduleList = noCompletedScheduleList;
              }
            }
          });
        },
        false,
        300,
        () => {
          this.$taskManager.removeTask(this.scheduleTask);
          this.loadData();
          this.$message(msgUtil.getCustomResultMsgData('시간 초과로 스케쥴 수행 결과를 확인하지 못하였습니다.', false));
        },
      );
      this.$taskManager.startTask(this.scheduleTask);
    },
    formatSchedulePeriod(row, column, value) {
      if (value === 'N') {
        return '한번';
      } else if (value === 'D') {
        return '매일';
      } else if (value === 'W') {
        return '매주';
      } else if (value === 'M') {
        return '월간';
      }
      return EMPTY_TEXT;
    },
    formatNextScheduleDatetime(record, column, value) {
      const period = record.schedulePeriod;
      if (period === 'N') {
        if (new Date() < new Date(record.scheduleStarttime)) {
          return getDateToString(record.scheduleStarttime);
        }
        return EMPTY_TEXT;
      } else if (period === 'D') {
        let nextTime = new Date().setHours(record.scheduleHour, record.scheduleMinute, 0, 0);

        if (nextTime < new Date()) {
          nextTime = moment(nextTime).add(1, 'days');
        }
        return getDateToString(nextTime);
      } else if (period === 'W') {
        const datetime = isEmpty(value) || new Date(value) < new Date() ? new Date() : new Date(value);

        const dates = record.scheduleIntervalDate.split(',');
        // 일요일 : moment - 0, java calendar: 1 -> 따라서 moment+1 = calendar의 요일
        const currentDate =
          moment(datetime)
            .clone()
            .day() + 1;
        const nextDate = find(dates, (date) => date > currentDate);
        const nextDay = parseInt(nextDate, 10) - 1; // moment사용으로 -1

        let nextTime = moment(datetime)
          .clone()
          .set({
            day: nextDay,
          })
          .toDate();

        if (nextTime < new Date()) {
          nextTime = moment()
            .add(1, 'weeks')
            .startOf('week')
            .set({
              day: parseInt(dates[0], 10) - 1,
            });
        } else if (moment().day() > nextDay) {
          nextTime = moment()
            .add(1, 'weeks')
            .startOf('week')
            .set({
              day: nextDay,
            });
        } else {
          nextTime = moment()
            .startOf('week')
            .set({
              day: nextDay,
            });
        }
        nextTime = nextTime.toDate().setHours(record.scheduleHour, record.scheduleMinute, 0, 0);
        return getDateToString(nextTime);
      } else if (period === 'M') {
        const datetime = isEmpty(value) || new Date(value) < new Date() ? new Date() : new Date(value);
        const months = record.scheduleIntervalMonth.split(',');
        const dates = record.scheduleIntervalDate.split(',');
        const currentMonth = datetime.getMonth() + 1;
        let nextMonth = parseInt(find(months, (month) => month >= currentMonth), 10);

        const currentDate = datetime.getDate();
        let nextDate = find(dates, (date) => date > currentDate);

        if (nextMonth > currentMonth) {
          nextDate = dates[0];
        }

        if (isEmpty(nextDate)) {
          nextMonth = months[months.indexOf(nextMonth.toString()) + 1];
          nextDate = dates[0];
        }

        let nextTime = moment(datetime).set({
          month: nextMonth - 1,
          date: nextDate,
          hour: record.scheduleHour,
          minute: record.scheduleMinute,
          second: 0,
        });
        if (nextMonth < currentMonth) {
          nextTime = nextTime.add(1, 'years');
        }

        return getDateToString(nextTime.toDate());
      }
      return EMPTY_TEXT;
    },
    /**
     * IP 대역 팝업창을 띄운다.
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    openIpScanPopup(record) {
      this.selectedScheduleData = record;
      this.$refs.ipscanPopup.open();
    },
    /**
     * 프로그레스바에 표현할 진행률을 계산한다.
     * @param  {[type]} processRate [description]
     * @author jda
     */
    setProcessRate(processRate) {
      if (isEmpty(processRate)) {
        return 0;
      }
      return processRate * 100;
    },
    /**
     * 네트워크 > 모델링 결과 팝업 제공
     * @param  {[type]}
     * @author Song
     */
    onClickDetailButton(row) {
      this.$refs.modelingResultPopup.open(row);
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/basic/DeviceRegistration/Network/Schedule.vue